import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import {
  ActivatedRoute,
  Router
} from '@angular/router';

import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import {
  Subject,
  throwError
} from 'rxjs';

import {
  catchError,
  finalize,
  switchMap,
  takeUntil
} from 'rxjs/operators';

import { ArticleDetails } from '../../core/models/article';
import { ArticleService } from '../../core/services/article.service';

@Component({
  selector: 'app-article-details',
  templateUrl: './article-details.component.html',
  styleUrls: ['./article-details.component.scss']
})
export class ArticleDetailsComponent
  implements OnInit, OnDestroy {

  article: ArticleDetails | null = null;

  articleHtml = '';

  currentLang = 'en';

  isLoading = false;
  errorMessage = '';

  readonly defaultImage =
    'assets/images/article-placeholder.jpg';

  readonly articleImagesUrl =
    'https://applicationform.aldawal.com/images/';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private articleService: ArticleService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.currentLang =
      this.translateService.currentLang ||
      localStorage.getItem('lang') ||
      this.translateService.defaultLang ||
      'en';

    this.listenToLanguageChanges();
    this.watchArticleSlug();
  }

  private listenToLanguageChanges(): void {
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.currentLang = event.lang;

        if (this.article) {
          this.updateDisplayedArticle();
        }
      });
  }

  private watchArticleSlug(): void {
    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),

        switchMap(params => {
          const slug = params.get('slug');

          if (!slug) {
            return throwError(
              () => new Error('Article slug is missing.')
            );
          }

          this.resetArticleState();

          return this.articleService
            .getArticleBySlug(slug)
            .pipe(
              finalize(() => {
                this.isLoading = false;
              })
            );
        }),

        catchError(error => {
          this.handleLoadingError(error);

          return throwError(() => error);
        })
      )
      .subscribe({
        next: article => {
          this.article = article;
          this.updateDisplayedArticle();
        },

        error: () => {
          // Error is already handled in catchError.
        }
      });
  }

  retryLoading(): void {
    const slug =
      this.route.snapshot.paramMap.get('slug');

    if (!slug) {
      return;
    }

    this.resetArticleState();

    this.articleService
      .getArticleBySlug(slug)
      .pipe(
        takeUntil(this.destroy$),

        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: article => {
          this.article = article;
          this.updateDisplayedArticle();
        },

        error: error => {
          this.handleLoadingError(error);
        }
      });
  }

  private resetArticleState(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.article = null;
    this.articleHtml = '';
  }

  private handleLoadingError(error: any): void {
    console.error(
      'Article details API error:',
      error
    );

    this.article = null;
    this.articleHtml = '';

    this.errorMessage =
      error?.error?.message ||
      error?.error?.title ||
      this.translateService.instant(
        'articleLoadingErrorMessage'
      ) ||
      'حدث خطأ أثناء تحميل المقال.';
  }

  private updateDisplayedArticle(): void {
    if (!this.article) {
      this.articleHtml = '';
      return;
    }

    this.articleHtml = this.prepareArticleHtml(
      this.getArticleBody()
    );

    const title = this.getArticleTitle();

    this.titleService.setTitle(
      title
        ? `Aldawal - ${title}`
        : 'Aldawal - Article'
    );
  }

  getArticleTitle(): string {
    if (!this.article) {
      return '';
    }

    if (this.currentLang === 'ar') {
      return (
        this.article.titleAr ||
        this.article.titleEn ||
        this.article.title ||
        ''
      );
    }

    return (
      this.article.titleEn ||
      this.article.titleAr ||
      this.article.title ||
      ''
    );
  }

  getArticleSummary(): string {
    if (!this.article) {
      return '';
    }

    if (this.currentLang === 'ar') {
      return (
        this.article.summaryAr ||
        this.article.summaryEn ||
        this.article.summary ||
        ''
      );
    }

    return (
      this.article.summaryEn ||
      this.article.summaryAr ||
      this.article.summary ||
      ''
    );
  }

  getArticleBody(): string {
    if (!this.article) {
      return '';
    }

    if (this.currentLang === 'ar') {
      return (
        this.article.htmlBodyAr ||
        this.article.htmlBodyEn ||
        this.article.htmlBody ||
        ''
      );
    }

    return (
      this.article.htmlBodyEn ||
      this.article.htmlBodyAr ||
      this.article.htmlBody ||
      ''
    );
  }

  getArticleImage(): string {
    const image = this.article?.featuredImage;

    if (!image) {
      return this.defaultImage;
    }

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:')
    ) {
      return image;
    }

    return `${this.articleImagesUrl}${image}`;
  }

  handleImageError(event: Event): void {
    const imageElement =
      event.target as HTMLImageElement;

    if (
      !imageElement.src.includes(
        'article-placeholder.jpg'
      )
    ) {
      imageElement.src = this.defaultImage;
    }
  }

  goToArticles(): void {
    this.router.navigate(['/Articles']);
  }

  private prepareArticleHtml(
    htmlBody: string
  ): string {

    if (!htmlBody) {
      return '';
    }

    let preparedHtml = htmlBody;

    if (
      preparedHtml.includes('&lt;') ||
      preparedHtml.includes('&gt;') ||
      preparedHtml.includes('&amp;')
    ) {
      preparedHtml =
        this.decodeHtmlEntities(preparedHtml);
    }

    preparedHtml = preparedHtml
      .replace(
        /<p>\s*(<(?:h[1-6]|ul|ol|blockquote|table)[^>]*>)/gi,
        '$1'
      )
      .replace(
        /(<\/(?:h[1-6]|ul|ol|blockquote|table)>)\s*<\/p>/gi,
        '$1'
      )
      .replace(
        /<p>\s*<p>/gi,
        '<p>'
      )
      .replace(
        /<\/p>\s*<\/p>/gi,
        '</p>'
      );

    return preparedHtml;
  }

  private decodeHtmlEntities(
    value: string
  ): string {

    const textarea =
      document.createElement('textarea');

    textarea.innerHTML = value;

    return textarea.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}